import React, { useRef, useState, useEffect } from "react";
import { FormHelperText } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./TextEditor.scss";

// Clipboard "text/html" carries the source markup's own indentation and line
// breaks, so reading its textContent yields leading/trailing newlines that get
// pasted into the editor as empty paragraphs. Normalize them away.
export const normalizePastedText = (text) =>
  (text || "").replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n").trim();

// Helper function to get character count from content
export const getCharacterCount = (content) => {
  if (!content) return 0;
  const div = document.createElement("div");
  div.innerHTML = content;
  const txt = div.textContent || div.innerText || "";
  return txt.length;
};

export default function TextEditor({
  id = "",
  readOnly = false,
  value,
  onChange,
  onLimitExceeded = null,
  isError = false,
  errorMsg = "",
  placeholder,
  maxCharacters = 2000,
  showCharacterLimit = true,
  shouldSetId = false,
  testId,
  toolbarOptions = [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
  ],
}) {
  const resolvedTestId = testId || id || undefined;
  const MAX_CHAR_LIMIT = maxCharacters;
  const quillRef = useRef(null);
  const scrollRef = useRef(0);

  const [editorValue, setEditorValue] = useState(value || "");
  const [charCount, setCharCount] = useState(0);
  const [pasteMessage, setPasteMessage] = useState("");
  const [showCharLimitError, setShowCharLimitError] = useState(false);

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const containsBase64Url = (content) => {
    if (!content) return false;
    const base64UrlPatterns = [
      /data:image\/[^;]+;base64,/gi,
      /data:application\/[^;]+;base64,/gi,
      /data:text\/[^;]+;base64,/gi,
      /data:audio\/[^;]+;base64,/gi,
      /data:video\/[^;]+;base64,/gi,
      /data:[^;]*;base64,/gi,
    ];
    return base64UrlPatterns.some((pattern) => pattern.test(content));
  };

  const showPasteMessage = (msg) => {
    setPasteMessage(msg);
    setTimeout(() => setPasteMessage(""), 3000);
  };

  // Called by ReactQuill onChange (typing, toolbar actions, default paste etc.)
  const handleChange = (content) => {
    scrollRef.current = window.scrollY; // save position before update
    const plainText = stripHtml(content);
    const length = plainText.length;

    if (containsBase64Url(content)) {
      showPasteMessage("Base64 URLs are not allowed.");
      // don't call onChange with the content that includes base64
      return;
    }

    if (!showCharacterLimit || length <= MAX_CHAR_LIMIT) {
      setEditorValue(content);
      setCharCount(length);
      setShowCharLimitError(length >= MAX_CHAR_LIMIT);

      const elementId = quillRef.current?.getEditor?.().root?.id || "";
      onChange?.(content, quillRef.current, elementId);
    } else {
      setShowCharLimitError(true);
      if (onLimitExceeded) {
        onLimitExceeded(length, MAX_CHAR_LIMIT);
      }
    }

    // restore scroll after DOM update
    setTimeout(() => window.scrollTo(0, scrollRef.current), 0);
  };

  // Keep internal editor value in sync when parent value changes
  useEffect(() => {
    if (value !== editorValue) {
      setEditorValue(value || "");
      const length = stripHtml(value || "").length;
      setCharCount(length);
      setShowCharLimitError(showCharacterLimit && length >= MAX_CHAR_LIMIT);
    }
  }, [value, showCharacterLimit, MAX_CHAR_LIMIT]);

  // Set the ID on the Quill editor root element only when shouldSetId is true
  useEffect(() => {
    if (shouldSetId && quillRef.current && id) {
      const editor = quillRef.current.getEditor();
      if (editor && editor.root) {
        editor.root.id = id;
      }
    }
  }, [shouldSetId, id]);

  // Handle paste robustly
  useEffect(() => {
    const quillInstance = quillRef.current?.getEditor?.();
    if (!quillInstance || !quillRef.current) return;

    const editor = quillInstance;

    const handlePaste = (e) => {
      try {
        const clipboardData = e.clipboardData || window.clipboardData;
        const pastedHtml = clipboardData.getData("text/html");
        const pastedPlain = clipboardData.getData("text/plain");
        const pastedData =
          (pastedHtml && pastedHtml.length ? pastedHtml : null) ||
          (pastedPlain && pastedPlain.length ? pastedPlain : "");

        if (!pastedData) return;

        if (containsBase64Url(pastedData)) {
          e.preventDefault();
          showPasteMessage("Base64 URLs are not allowed.");
          return;
        }

        // Use editor.live methods (avoid reading editorValue state)
        const currentTextRaw = editor.getText ? editor.getText() : "";
        const currentText = currentTextRaw.replace(/\n$/, "");
        const selection = editor.getSelection();
        const selIndex = selection?.index ?? editor.getLength() - 1;
        const selLength = selection?.length || 0;

        // Insert from text/plain when the clipboard has it: stripping the HTML
        // flavour keeps the markup's formatting whitespace, which Quill turns
        // into blank lines before/after the pasted content.
        const plainText = normalizePastedText(
          pastedPlain && pastedPlain.length ? pastedPlain : stripHtml(pastedData)
        );

        // Compute available chars
        const newLength = currentText.length - selLength + plainText.length;
        let toInsert = plainText;

        if (showCharacterLimit && newLength > MAX_CHAR_LIMIT) {
          const availableChars =
            MAX_CHAR_LIMIT - currentText.length + selLength;
          if (availableChars <= 0) {
            // nothing to insert
            e.preventDefault();
            showPasteMessage(`Character limit of ${MAX_CHAR_LIMIT} reached.`);
            setShowCharLimitError(true);
            onLimitExceeded?.(newLength, MAX_CHAR_LIMIT);
            return;
          }
          toInsert = plainText.substring(0, availableChars);
        }

        // Prevent default and do a deterministic insert (no race)
        e.preventDefault();

        // delete current selection (if any) and insert the text
        editor.deleteText(selIndex, selLength);
        // insertText will place plain text; if you want to keep HTML formatting you'd need to parse/convert
        editor.insertText(selIndex, toInsert);

        // Immediately read content and update states + parent
        const newContent = editor.root.innerHTML;
        setEditorValue(newContent);
        setCharCount(stripHtml(newContent).length);
        setShowCharLimitError(
          showCharacterLimit && stripHtml(newContent).length >= MAX_CHAR_LIMIT
        );

        // call onChange synchronously so parent gets the update
        onChange?.(newContent, editor, editor.root.id || "");

        if (showCharacterLimit && newLength > MAX_CHAR_LIMIT) {
          onLimitExceeded?.(newLength, MAX_CHAR_LIMIT);
        }
      } catch (err) {
        // swallow so editor doesn't break — optionally console.log for debug
        // console.error("paste handler error", err);
      }
    };

    editor.root.addEventListener("paste", handlePaste, true);
    return () => {
      editor.root.removeEventListener("paste", handlePaste, true);
    };
  }, [quillRef, MAX_CHAR_LIMIT, showCharacterLimit, onLimitExceeded, onChange]);

  const handleBlur = () => {
    const editorInstance = quillRef.current?.getEditor?.();
    const content = editorInstance?.root?.innerHTML || "";
    const elementId = editorInstance?.root?.id || "";
    onChange?.(content, editorInstance, elementId);
  };

  const formats = ["bold", "italic", "underline", "list", "bullet", "align"];

  return (
    <div className="overrideQuillEditor" data-testid={resolvedTestId ? `${resolvedTestId}-wrapper` : undefined}>
      {isError && stripHtml(editorValue).length === 0 && (
        <FormHelperText className="errorText">{errorMsg}</FormHelperText>
      )}

      <ReactQuill
        id={id}
        ref={quillRef}
        value={editorValue}
        onChange={handleChange}
        onBlur={handleBlur}
        modules={{ toolbar: toolbarOptions }}
        formats={formats}
        placeholder={placeholder || "Start typing..."}
        theme="snow"
        readOnly={readOnly}
      />

      {showCharacterLimit && (
        <div className="char-limit-msg">
          {charCount} / {MAX_CHAR_LIMIT} Characters
        </div>
      )}

      {showCharLimitError && showCharacterLimit && (
        <div
          className="error"
          style={{ color: "red", fontSize: "14px", marginTop: "4px" }}
        >
          Character limit exceeded, you can only enter {MAX_CHAR_LIMIT}{" "}
          characters
        </div>
      )}

      {pasteMessage && <div className="editor-error-msg">{pasteMessage}</div>}
    </div>
  );
}
